import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtAmount, fmtDate, StageBadge } from "@/lib/util";
import CurrencyIcon from "@/components/CurrencyIcon";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckSquare, ClockCounterClockwise, Buildings, MagnifyingGlass, WhatsappLogo } from "@phosphor-icons/react";
import WhatsAppExport from "@/components/WhatsAppExport";

// Daily accounting view: shows every paid/verified request and lets the user
// stamp an accounting_date one-by-one or in bulk. Backed by GET /payments
// (stage=paid or verified) + POST /payments/bulk-set-accounting-date.
export default function Accounting() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("awaiting"); // awaiting | accounted | all
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkDate, setBulkDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [waOpen, setWaOpen] = useState(false);

  useEffect(() => { api.get("/brands").then((r) => setBrands(r.data.filter((b) => b.is_active))); }, []);

  const refetch = async () => {
    setLoading(true);
    try {
      // We want paid + verified only. `/payments` accepts a single stage — so
      // we fetch both and merge. Also apply optional brand & date filters.
      const base = { limit: 500 };
      if (brandId) base.brand_id = brandId;
      if (dateFrom) base.date_from = dateFrom;
      if (dateTo) base.date_to = `${dateTo}T23:59:59`;
      if (q) base.q = q;
      const [paid, verified] = await Promise.all([
        api.get("/payments", { params: { ...base, stage: "paid" } }),
        api.get("/payments", { params: { ...base, stage: "verified" } }),
      ]);
      const merged = [...(paid.data || []), ...(verified.data || [])]
        .sort((a, b) => (b.paid_at || b.created_at || "").localeCompare(a.paid_at || a.created_at || ""));
      // Filter by accounting status
      const filtered = merged.filter((r) => {
        if (status === "awaiting")  return !r.accounting_date;
        if (status === "accounted") return !!r.accounting_date;
        return true;
      });
      setRows(filtered);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refetch(); }, [brandId, q, status, dateFrom, dateTo]);

  const totals = useMemo(() => {
    const byCur = {};
    let awaitingCount = 0;
    for (const r of rows) {
      const c = r.currency_code;
      byCur[c] = (byCur[c] || 0) + (r.net_amount || 0);
      if (!r.accounting_date) awaitingCount++;
    }
    return { byCur, awaitingCount };
  }, [rows]);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const toggleAll = () => setSelected((s) => (
    s.size === rows.length && rows.length > 0 ? new Set() : new Set(rows.map((r) => r.id))
  ));

  const markOne = async (row, date) => {
    setBusy(true);
    try {
      await api.post("/payments/bulk-set-accounting-date", { ids: [row.id], accounting_date: date });
      toast.success(date ? `${row.reference} · marked ${date}` : `${row.reference} · cleared`);
      await refetch();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const markSelected = async () => {
    if (selected.size === 0) return;
    if (!bulkDate) { toast.error("Pick a date first"); return; }
    setBusy(true);
    try {
      const r = await api.post("/payments/bulk-set-accounting-date", { ids: [...selected], accounting_date: bulkDate });
      toast.success(`Stamped ${r.data.modified} of ${selected.size} with ${bulkDate}`);
      setSelected(new Set());
      await refetch();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const clearSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Clear the accounting date on ${selected.size} row(s)?`)) return;
    setBusy(true);
    try {
      await api.post("/payments/bulk-set-accounting-date", { ids: [...selected], accounting_date: null });
      toast.success("Cleared");
      setSelected(new Set());
      await refetch();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1700px]">
      <header className="mb-6">
        <div className="overline">Accounting</div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-1">
          <span className="bg-gradient-to-br from-amber-600 to-slate-600 bg-clip-text text-transparent">Daily books</span>
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Every paid or verified request — mark them as booked in your accounting system with one click.
        </p>
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KpiCard
          icon={<ClockCounterClockwise weight="duotone" size={22} className="text-amber-700" />}
          label="Awaiting accounting"
          value={totals.awaitingCount}
          testid="acc-kpi-awaiting"
        />
        {Object.entries(totals.byCur).slice(0, 3).map(([c, v]) => (
          <KpiCard
            key={c}
            icon={<CurrencyIcon code={c} size={22} />}
            label={`${c} in view`}
            value={fmtAmount(v)}
            mono
            testid={`acc-kpi-${c}`}
          />
        ))}
      </div>

      {/* Filters */}
      <div className="glass rounded-2xl p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input data-testid="acc-search" placeholder="Vendor, reference, amount…" value={q} onChange={(e) => setQ(e.target.value)}
                     className="pl-9 rounded-xl bg-white/70 border-white/60 h-10" />
            </div>
          </div>
          <div className="w-44">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="acc-status" className="rounded-xl bg-white/70 border-white/60 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="awaiting">Awaiting accounting</SelectItem>
                <SelectItem value="accounted">Already accounted</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-48">
            <Select value={brandId || "all"} onValueChange={(v) => setBrandId(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="acc-brand" className="rounded-xl bg-white/70 border-white/60 h-10">
                <SelectValue placeholder="Brand" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All brands</SelectItem>
                {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold pl-1 block">Paid from</label>
            <Input data-testid="acc-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                   className="rounded-xl bg-white/70 border-white/60 h-10 w-40" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold pl-1 block">Paid to</label>
            <Input data-testid="acc-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                   className="rounded-xl bg-white/70 border-white/60 h-10 w-40" />
          </div>
        </div>
      </div>

      {/* Bulk toolbar */}
      {selected.size > 0 && (
        <div data-testid="acc-bulk-toolbar" className="mb-3 flex flex-wrap items-center gap-3 rounded-2xl bg-amber-50/80 border border-amber-300 p-3">
          <div className="text-sm font-semibold text-amber-900">{selected.size} selected</div>
          <button data-testid="acc-bulk-whatsapp" onClick={() => setWaOpen(true)}
                  className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700">
            <WhatsappLogo size={14} weight="fill" /> WhatsApp msg
          </button>
          <Input data-testid="acc-bulk-date" type="date" value={bulkDate} onChange={(e) => setBulkDate(e.target.value)} className="h-9 w-40" />
          <button data-testid="acc-bulk-apply" onClick={markSelected} disabled={busy || !bulkDate}
                  className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50">
            <CheckSquare weight="fill" size={14} className="inline mr-1" /> Stamp {selected.size} as accounted
          </button>
          <button data-testid="acc-bulk-clear" onClick={clearSelected} disabled={busy}
                  className="px-3 py-1.5 rounded-lg border border-amber-300 text-amber-800 text-sm font-semibold hover:bg-amber-100">
            Clear
          </button>
          <button onClick={() => setSelected(new Set())} className="text-xs text-slate-600 underline ml-auto">Deselect all</button>
        </div>
      )}

      {/* List */}
      <div className="glass rounded-2xl overflow-hidden">
        <table className="w-full text-sm table-fixed">
          <colgroup>
            <col style={{ width: "34px" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "auto" }} />
          </colgroup>
          <thead className="bg-white/40">
            <tr className="text-left">
              <th className="px-2 py-2">
                <input data-testid="acc-select-all" type="checkbox"
                       checked={rows.length > 0 && selected.size === rows.length}
                       onChange={toggleAll} className="accent-amber-600 cursor-pointer" />
              </th>
              {["Ref","Paid date","Vendor","Brand","Amount","Stage","Accounting date",""].map((h,i) => (
                <th key={i} className="px-3 py-2 overline">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-slate-500">Loading…</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} data-testid={`acc-row-${r.reference}`} className={`${selected.has(r.id) ? "bg-amber-50/50" : ""} ${r.accounting_date ? "" : "bg-white/40"}`}>
                <td className="px-2 py-3 align-top">
                  <input data-testid={`acc-select-${r.reference}`} type="checkbox" checked={selected.has(r.id)}
                         onChange={() => toggle(r.id)} className="accent-amber-600 cursor-pointer mt-1" />
                </td>
                <td className="px-3 py-3 align-top">
                  <Link to={`/requests/${r.id}`} className="font-mono text-xs text-amber-700 font-semibold hover:underline">{r.reference}</Link>
                </td>
                <td className="px-3 py-3 align-top text-xs text-slate-700">
                  {r.paid_at ? fmtDate(r.paid_at).split(",")[0] : "—"}
                </td>
                <td className="px-3 py-3 align-top text-sm truncate" title={r.vendor_name}>{r.vendor_name}</td>
                <td className="px-3 py-3 align-top">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-900 border border-amber-300">
                    <Buildings weight="fill" size={10} /> {r.brand_name || "—"}
                  </span>
                </td>
                <td className="px-3 py-3 align-top">
                  <div className="flex items-center gap-1.5 font-mono font-semibold">
                    <CurrencyIcon code={r.currency_code} size={12} />
                    {fmtAmount(r.net_amount, r.currency_code)}
                  </div>
                </td>
                <td className="px-3 py-3 align-top"><StageBadge stage={r.stage} approvalStep={r.approval_step} /></td>
                <td className="px-3 py-3 align-top font-mono text-xs">
                  {r.accounting_date
                    ? <span className="inline-flex items-center gap-1 text-emerald-800 font-semibold"><CheckSquare weight="fill" size={12} /> {r.accounting_date}</span>
                    : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-3 py-3 align-top text-right">
                  {!r.accounting_date ? (
                    <button data-testid={`acc-mark-today-${r.reference}`}
                            onClick={() => markOne(r, new Date().toISOString().slice(0, 10))}
                            disabled={busy}
                            className="text-xs font-bold px-2.5 py-1 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap">
                      Mark today
                    </button>
                  ) : (
                    <button data-testid={`acc-unmark-${r.reference}`}
                            onClick={() => markOne(r, null)}
                            disabled={busy}
                            className="text-xs font-semibold text-slate-500 hover:text-red-700 hover:underline">
                      Undo
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-14 text-center text-sm text-slate-500">
                {status === "awaiting" ? "You're all caught up 🎉" : "No matching payments in this range."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-3 text-xs text-slate-500 font-mono">{rows.length} payment{rows.length === 1 ? "" : "s"}</div>

      {rows.length > 0 && selected.size === 0 && (
        <div className="mt-2 flex justify-end">
          <button data-testid="acc-wa-visible" onClick={() => setWaOpen(true)}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-white/70 border border-emerald-300 text-emerald-800 text-xs font-bold hover:bg-emerald-50">
            <WhatsappLogo size={13} weight="fill" /> WhatsApp msg · {rows.length} visible
          </button>
        </div>
      )}

      <WhatsAppExport
        open={waOpen}
        onOpenChange={setWaOpen}
        rows={selected.size > 0 ? rows.filter((r) => selected.has(r.id)) : rows}
        defaultTitle={status === "awaiting" ? "Entries Pending" : status === "accounted" ? "Entries Accounted" : "Payment Entries"}
      />
    </div>
  );
}

function KpiCard({ icon, label, value, mono, testid }) {
  return (
    <div data-testid={testid} className="glass rounded-2xl p-4 shimmer-border">
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        {icon}
      </div>
      <div className={`font-heading font-black text-2xl mt-2 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
